import type { Salutation } from './salutation';
import {
  getSnippetDocumentType,
  type SnippetDocumentType,
  type TextSnippetCategory,
} from '../types/textSnippets';

/**
 * Token catalogue for text snippets — the client-side mirror of OfferSnippetTokens /
 * InvoiceSnippetTokens in the Api repo (ADR-0020). The backend is authoritative for
 * rendering; this module only drives the token helper, the sample preview and the MSW mocks.
 */

export const COMMON_TOKENS = [
  'CustomerName',
  'CustomerNumber',
  'ObjectName',
  'ContactSalutation',
  'ContactPersonName',
  'Greeting',
] as const;

export const OFFER_TOKENS = ['OfferNumber', 'OfferDate', 'ValidUntilDate'] as const;

export const INVOICE_TOKENS = [
  'InvoiceNumber',
  'InvoiceDate',
  'DueDate',
  'ServiceDateFrom',
  'ServiceDateTo',
  'BillingPeriod',
] as const;

const TAX_TOKENS = ['TaxRate'] as const;

export type SnippetToken =
  | (typeof COMMON_TOKENS)[number]
  | (typeof OFFER_TOKENS)[number]
  | (typeof INVOICE_TOKENS)[number]
  | (typeof TAX_TOKENS)[number];

/** Token → value. A key that is absent stays raw in the text; `null`/`undefined` renders empty. */
export type SnippetTokenMap = Partial<Record<SnippetToken, string | null | undefined>>;

const TOKENS_BY_DOCUMENT_TYPE: Record<SnippetDocumentType, readonly SnippetToken[]> = {
  offer: [...COMMON_TOKENS, ...OFFER_TOKENS, ...TAX_TOKENS],
  invoice: [...COMMON_TOKENS, ...INVOICE_TOKENS, ...TAX_TOKENS],
};

export function getTokensForDocumentType(documentType: SnippetDocumentType): readonly SnippetToken[] {
  return TOKENS_BY_DOCUMENT_TYPE[documentType];
}

export function getTokensForCategory(category: TextSnippetCategory): readonly SnippetToken[] {
  return getTokensForDocumentType(getSnippetDocumentType(category));
}

/**
 * Mirror of TextSnippetRenderer.Render (Api repo): ordinal `{Key}` replace per map entry,
 * `null`/`undefined` become empty, keys missing from the map are left untouched.
 */
export function replaceTokens(body: string, map: SnippetTokenMap): string {
  if (!body) return body;
  let result = body;
  for (const [key, value] of Object.entries(map)) {
    result = result.split(`{${key}}`).join(value ?? '');
  }
  return result;
}

// why: German-only helpers that mirror SalutationFormatter (Api repo) so mock/sample output
// looks like the real thing. Product UI text goes through i18n; these do not, on purpose.
export function salutationTitleDe(salutation: Salutation | null | undefined): string {
  if (salutation === 'mr') return 'Herr';
  if (salutation === 'ms') return 'Frau';
  return '';
}

export const FALLBACK_GREETING_DE = 'Sehr geehrte Damen und Herren';

/** "Herr Max Muster" — empty when the name is blank (SalutationFormatter.AddressLine). */
export function addressLineDe(
  salutation: Salutation | null | undefined,
  name: string | null | undefined,
): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '';
  const title = salutationTitleDe(salutation);
  return title ? `${title} ${trimmed}` : trimmed;
}

/** Letter greeting without the trailing comma (SalutationFormatter.Greeting). */
export function greetingDe(
  salutation: Salutation | null | undefined,
  name: string | null | undefined,
): string {
  const trimmed = (name ?? '').trim();
  // why: a title in front of an empty name reads broken, so a blank name always falls back.
  if (!trimmed) return FALLBACK_GREETING_DE;
  switch (salutation) {
    case 'mr':
      return `Sehr geehrter Herr ${trimmed}`;
    case 'ms':
      return `Sehr geehrte Frau ${trimmed}`;
    case 'diverse':
      return `Guten Tag ${trimmed}`;
    default:
      return FALLBACK_GREETING_DE;
  }
}
